import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { FormItem, FormLabel, FormControl, FormMessage } from "../ui/form";
import { Loader2 } from "lucide-react";
import { ROLES } from "../../lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

import {
  useGetUserQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
} from "../../store/api/userApi";

const formSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email"),
  role: z.string().min(1, "Role is required"),
  password: z.string().optional(),
});

const UserFormModal = ({ isOpen, onClose, id = null }) => {
  const isEditMode = id !== null;

  const {
    data: userData,
    isLoading: isLoadingUser,
    error: fetchError,
  } = useGetUserQuery(id, {
    skip: !isOpen || !isEditMode,
  });

  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();

  const isSubmitting = isCreating || isUpdating;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      role: "user",
    },
  });

  useEffect(() => {
    if (isEditMode && userData) {
      reset({
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        role: userData.role,
        password: "",
      });
    } else if (!isEditMode) {
      reset({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        role: "user",
      });
    }
  }, [userData, reset, isEditMode]);

  const onSubmit = async (values) => {
    try {
      if (isEditMode) {
        await updateUser({
          id,
          ...values,
        }).unwrap();
      } else {
        await createUser(values).unwrap();
      }
      onClose();
    } catch (error) {
      console.error(
        `Failed to ${isEditMode ? "update" : "create"} user:`,
        error
      );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditMode ? "Edit User" : "Create User"}</DialogTitle>
        </DialogHeader>

        {isEditMode && isLoadingUser ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="ml-2">Loading user data...</span>
          </div>
        ) : isEditMode && fetchError ? (
          <div className="text-center py-8 text-destructive">
            Error loading user data. Please try again.
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* FIRST NAME */}
            <FormItem>
              <FormLabel>First Name</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  {...register("firstName")}
                  disabled={isSubmitting}
                />
              </FormControl>
              {errors.firstName && (
                <FormMessage>{errors.firstName.message}</FormMessage>
              )}
            </FormItem>

            {/* LAST NAME */}
            <FormItem>
              <FormLabel>Last Name</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  {...register("lastName")}
                  disabled={isSubmitting}
                />
              </FormControl>
              {errors.lastName && (
                <FormMessage>{errors.lastName.message}</FormMessage>
              )}
            </FormItem>

            {/* EMAIL */}
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  {...register("email")}
                  disabled={isSubmitting}
                />
              </FormControl>
              {errors.email && (
                <FormMessage>{errors.email.message}</FormMessage>
              )}
            </FormItem>

            {/* PASSWORD — ONLY IN CREATE MODE */}
            {!isEditMode && (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    {...register("password")}
                    disabled={isSubmitting}
                  />
                </FormControl>
                {errors.password && (
                  <FormMessage>{errors.password.message}</FormMessage>
                )}
              </FormItem>
            )}

            {/* ROLE DROPDOWN */}
            <FormItem>
              <FormLabel>Role</FormLabel>
              <FormControl>
                <select
                  {...register("role")}
                  disabled={isSubmitting}
                  className="border rounded-md px-3 h-10"
                >
                  {Object.values(ROLES).map((role) => (
                    <option key={role} value={role}>
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </option>
                  ))}
                </select>
              </FormControl>
              {errors.role && <FormMessage>{errors.role.message}</FormMessage>}
            </FormItem>

            {/* FOOTER BUTTONS */}
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>

              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isEditMode ? "Saving..." : "Creating..."}
                  </>
                ) : isEditMode ? (
                  "Save Changes"
                ) : (
                  "Create"
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserFormModal;
